import React, { useEffect } from 'react';
import * as _ from 'lodash';
import Delta from 'quill-delta';
import { logError } from 'utils/logger';
import { Event } from 'json-rules-engine';
import { RenderWithCondition } from 'components/shared/Conditional/RenderWithCondition';
import { AftermarketProductQualifiedEventOptions } from '@trnsact/business-criteria/dist/src/models/rule-event/aftermarket/product-qualified/aftermarket-product-qualified-event-options';
import { DynamicFields } from './DynamicFields';
import { allFacts } from '../../../../../constants';
import { useRulesEngine } from '../../../../../context';
import { ProductConfigurationResults } from './ProductConfigurationResults';
import { ProposalCardContainer } from '../cardContainer/ProposalCardContainer';
import { collectMinMaxPriceConfiguration, productsCalculationManager } from '../../../../../lib';
import { MarkupType, ProposalProductCardByVendorProps, ProposalProductCardModes } from '../../../../../types';

export const Card = ({
  mode,
  product,
  equipmentCost,
  prisingConfig,
  type = 'simple',
  factsToCheck = [],
  factsSkipped = [],
  hideFieldsForRender,
  productConfiguration,
  isNeedRunJsonEngine = 0,
  updateProductConfiguration,
}: ProposalProductCardByVendorProps) => {
  const { engine, handleAddProductsRules, analyzeFailureResults, failedRules } = useRulesEngine();

  Object.values(allFacts).forEach(factKey => {
    const factFromConfig = productConfiguration[factKey];

    if (!factFromConfig) return;
    engine.addFact(factKey, factFromConfig);
  }, {});

  useEffect(() => {
    handleAddProductsRules(product);
  }, [handleAddProductsRules, product]);

  const createProductCoverageDelta = (coverageDetails: any, coverageTerm: string) => {
    try {
      const coverageDelta = new Delta();
      if (coverageDetails?.ops) {
        coverageDetails.ops.forEach((op: any) => {
          if (op.insert) {
            coverageDelta.insert(op.insert);
          }
        });
      }
      coverageDelta.insert(
        `Coverage Term: ${coverageTerm}${parseInt(coverageTerm).toString() === coverageTerm ? ' months' : ''}\n`
      );
      return { ops: [...coverageDelta.ops] };
    } catch (e: any) {
      console.log(`Error during createProductCoverageDelta: ${e.message}`);
      return coverageDetails;
    }
  };

  function handleGetDynamicProducts() {
    engine.removeRule('NTP Product Dynamic Coverage');
  }

  function handleOverrideStringFactOptions(event: Event) {
    try {
      const overrides = event.params?.overrides;
      if (!overrides) return;

      overrides.forEach((override: any) => {
        const factName = override.fact;
        const index = factsToCheck.findIndex(f => f.factKey === factName);
        const fact = factsToCheck[index];
        if (fact?.type === 'string') {
          factsToCheck[index].options = override.values;
        }
      });
    } catch (e) {
      console.log(`Error during OVERRIDE_STRING_FACT_OPTIONS: ${(e as any).message}`);
    }
  }

  function handleAftermarketProductQualified(event: Event) {
    const typedEvent = event as AftermarketProductQualifiedEventOptions;
    const params = typedEvent.params ?? {};

    const dealerCostValue = params.DEALER_COST ?? 0;
    const markupValue = params.MARKUP?.AMOUNT ?? 0;
    const rateFactor = (params as any).RATE_FACTOR ?? 0;
    const retailCostValue = params.SUGGESTED_RETAIL_PRICE ?? 0;
    const markupType = params.MARKUP?.TYPE ?? 'FIXED';

    const dealerCost = rateFactor
      ? productsCalculationManager.calculateCostByRateFactor(rateFactor, equipmentCost)
      : dealerCostValue;

    const markupAmount = _.isNumber(markupValue)
      ? markupValue
      : productsCalculationManager.calculateMarkupFlat(retailCostValue, dealerCost);

    const retailCost = calculateRetailCost(rateFactor, retailCostValue, markupType, dealerCost, markupAmount);

    let catalogDetails = { ...product.aftermarketProduct?.aftermarketProductCatalogDetails };

    if (productConfiguration.COVERAGE_TERM && catalogDetails) {
      try {
        catalogDetails.coverageDetails = createProductCoverageDelta(
          catalogDetails.coverageDetails,
          productConfiguration.COVERAGE_TERM
        );
      } catch (error) {
        logError(error);
      }
    }

    updateProductConfiguration({
      retailCost,
      cost: dealerCost,
      customPrice: false,
      isPricingValid: true,
      markup: {
        markup: markupAmount,
        type: markupType === 'FIXED' ? MarkupType.Flat : MarkupType.Percentage,
      },
      rateFactor: rateFactor || productsCalculationManager.calculateRateFactor(dealerCost, equipmentCost),
      aftermarketProduct: {
        aftermarketProductCatalogDetails: catalogDetails,
      },
      ...collectMinMaxPriceConfiguration(typedEvent, retailCost),
    });
  }

  const handelRunEngine = () => {
    Object.entries(productConfiguration).forEach(([field, value]) => {
      engine.addFact(field, value);
    });

    engine
      .run()
      .then(engineResults => {
        if (!engineResults.events.length) {
          analyzeFailureResults(engineResults);
          updateProductConfiguration({ isPricingValid: false });
          return;
        }

        const actionsByEvent: Record<string, (event: Event) => void> = {
          AFTERMARKET_PRODUCT_GET_DYNAMIC_PRODUCTS: handleGetDynamicProducts,
          OVERRIDE_STRING_FACT_OPTIONS: handleOverrideStringFactOptions,
          AFTERMARKET_PRODUCT_QUALIFIED: handleAftermarketProductQualified,
        };

        engineResults.events.forEach(event => {
          if (typeof actionsByEvent[event.type] === 'function') actionsByEvent[event.type](event);
        });
      })
      .catch(logError);
  };

  // TODO bad approach: should encapsulate this handler to context
  useEffect(() => {
    const isNecessaryRunEngine = !productConfiguration?.customPrice;

    if (isNecessaryRunEngine) handelRunEngine();
  }, [isNeedRunJsonEngine]);

  return (
    <ProposalCardContainer
      type={type}
      mode={mode}
      proposalProduct={product}
      factsSkipped={factsSkipped}
      prisingConfig={prisingConfig}
      equipmentCost={equipmentCost}
      logo={product.aftermarketProduct?.productLogoUrl}
      productConfiguration={productConfiguration}
      updateProductConfiguration={updateProductConfiguration}
    >
      <RenderWithCondition condition={mode === ProposalProductCardModes.Desking}>
        <ProductConfigurationResults
          factsToCheck={factsToCheck}
          failedRules={failedRules}
          productConfiguration={productConfiguration}
        />
      </RenderWithCondition>

      <DynamicFields
        mode={mode}
        factsToCheck={factsToCheck}
        hideFieldsForRender={hideFieldsForRender}
        productConfiguration={productConfiguration}
        updateProductConfiguration={updateProductConfiguration}
      />
    </ProposalCardContainer>
  );
};

function calculateRetailCost(
  rateFactor: number,
  retailCostValue: number,
  markupType: string,
  dealerCost: number,
  markupAmount: number
): number {
  if (!rateFactor && retailCostValue !== 0) {
    return retailCostValue;
  }

  if (markupType === 'FIXED') {
    return productsCalculationManager.calculateRetailCostByFlatMarkup(dealerCost, markupAmount);
  }

  return productsCalculationManager.calculateRetailCostByPercentageMarkup(dealerCost, markupAmount);
}
